/**
 * Which of the things being presented the viewer is looking at.
 *
 * `useStageContent` collects every live source — screen shares, the whiteboard,
 * a shared document, a shared video — into one list and lets the viewer page
 * between them. This module owns the other half: which one the stage shows, and
 * when that changes on its own.
 *
 * The rule it exists to enforce is "starting something moves the viewer". That
 * was always the documented intent, and the code did not do it: selection was a
 * single key, and a key that still matched an item won every comparison. So the
 * first thing presented took the stage and then nothing ever displaced it. Share
 * a document, then share your screen, and the screen went out over RTP, appeared
 * as a chip in the switcher, and was shown to nobody — every client, the
 * presenter's included, stayed on the document. The only way to reach it was to
 * click its chip, and viewers had no reason to think there was anything to click.
 *
 * The fallback underneath was wrong in the same direction: "the newest arrival"
 * was spelled `items[items.length - 1]`, but that list is ordered by KIND —
 * screens, whiteboard, document, video — not by when things started. A screen
 * share is always at the front, so it was the one item the last-index rule could
 * never return.
 *
 * ## Arrival order, and why acknowledgement is separate from it
 *
 * Every key gets a tick when it first appears. Moving the viewer to whatever has
 * the highest tick would fix the bug and break the switcher: choosing an older
 * source for yourself would snap straight back to the newest one, every render.
 *
 * So arrival is recorded separately from `acked` — the highest tick the viewer
 * has already been shown or has decided about. Anything above that line is new
 * and takes the stage; anything below it has been dealt with, whether by being
 * watched, chosen past, or followed past. Picking an old source raises the line
 * to the present, which is what makes a deliberate choice stick.
 *
 * Every function is pure and returns a new `StageSelection`; the caller holds it
 * and passes it back. Applying the same list twice is a no-op, so a
 * double-invoked render cannot double-advance the tick.
 */

export type StageSelection = {
  /** Key -> the tick at which it appeared. Departed keys are dropped. */
  readonly arrivals: ReadonlyMap<string, number>
  /** Monotonic counter. Only the ORDER of these values is meaningful. */
  readonly tick: number
  /**
   * The highest arrival tick the viewer has already been moved to, or has
   * decided about by choosing something else. Nothing at or below this line
   * will pull them again.
   */
  readonly acked: number
  /** The item on the stage, or null when nothing is being presented. */
  readonly selectedKey: string | null
  /** On the host's pick, and not since moved off it. */
  readonly following: boolean
}

export const emptySelection = (): StageSelection => ({
  arrivals: new Map(),
  tick: 0,
  acked: 0,
  selectedKey: null,
  following: false,
})

/**
 * Fold the current list of presentable keys in.
 *
 * Registers whatever is new, forgets whatever has ended, and moves the viewer
 * if anything arrived above the acknowledged line. `keys` is in the caller's
 * display order; ties among keys appearing in the same pass are broken by it,
 * so two shares that land together put the viewer on the later one.
 *
 * A key that leaves and comes back is genuinely new — a share that stopped and
 * restarted is a new thing to look at, and gets a fresh tick.
 */
export function reconcile(prev: StageSelection, keys: readonly string[]): StageSelection {
  // Nothing is being presented. `following` a pick that no longer exists has to
  // go, or the viewer would be re-snapped the next time anyone shared anything.
  //
  // `acked` is deliberately left where it is. The reset that used to sit here
  // looked like it was protecting the next share from arriving below the line
  // and being shown to nobody, and could not: `tick` is carried across, ticks
  // are never reused, and `acked` only ever holds a tick already issued — so
  // whatever starts next is above it by construction. Mutation testing is what
  // showed the reset could not change an outcome.
  if (keys.length === 0) {
    return prev.selectedKey === null && !prev.following && prev.arrivals.size === 0
      ? prev
      : { ...prev, arrivals: new Map(), selectedKey: null, following: false }
  }

  const present = new Set(keys)
  let arrivals = prev.arrivals
  let tick = prev.tick
  let changed = false

  // Copy-on-write: a pass where nothing started or ended must return `prev`
  // untouched, so callers memoising on identity are not woken to say nothing.
  const mutate = () => {
    if (!changed) {
      arrivals = new Map(arrivals)
      changed = true
    }
    return arrivals as Map<string, number>
  }

  for (const key of prev.arrivals.keys()) {
    if (!present.has(key)) mutate().delete(key)
  }
  for (const key of keys) {
    if (!arrivals.has(key)) {
      tick += 1
      mutate().set(key, tick)
    }
  }

  const { acked } = prev
  let { selectedKey, following } = prev

  // ── Anything new takes the stage ──────────────────────────────────────────
  // Highest tick above the line, which after the loop above is the last key to
  // have arrived. Compared against `acked` rather than against the selection's
  // own tick, so a source the viewer deliberately chose past cannot pull them
  // back the moment something else ends.
  let newest: string | null = null
  let newestTick = acked
  for (const key of keys) {
    const at = arrivals.get(key)!
    if (at > newestTick) {
      newest = key
      newestTick = at
    }
  }

  if (newest !== null) {
    // Being pulled onto something new is a break from the host as surely as
    // choosing is: the viewer is no longer on the host's pick, and saying they
    // are would be a lie the badge tells.
    return { arrivals, tick, acked: newestTick, selectedKey: newest, following: false }
  }

  // ── Otherwise hold the selection, unless it has ended ──────────────────────
  if (selectedKey === null || !present.has(selectedKey)) {
    // Fall back to the most recently arrived of what is left — the thing that
    // happened last, which is what the viewer was most likely already on their
    // way to. Everything here is at or below the line by construction, so this
    // cannot loop.
    let best: string | null = null
    let bestTick = -1
    for (const key of keys) {
      const at = arrivals.get(key)!
      if (at > bestTick) {
        best = key
        bestTick = at
      }
    }
    selectedKey = best
    following = false
  }

  return prev.arrivals === arrivals &&
    prev.acked === acked &&
    prev.selectedKey === selectedKey &&
    prev.following === following
    ? prev
    : { arrivals, tick, acked, selectedKey, following }
}

/**
 * The viewer picked something for themselves.
 *
 * The choice has to stick against sources NEWER than the one chosen — that is
 * the case worth protecting, since reading a document while someone else's
 * screen share is live must not snap back to the share on the next render. It
 * does so without touching `acked`, because `reconcile` has already raised that
 * line to the newest thing present: a source newer than the selection is a
 * source the viewer was pulled onto and has now chosen to leave. Raising it
 * again here was written first and removed once mutation testing showed it
 * could not change an outcome.
 *
 * An unknown key is ignored rather than selected: the chip for it is gone, and
 * pointing the stage at nothing would blank it.
 */
export function choose(prev: StageSelection, key: string): StageSelection {
  if (!prev.arrivals.has(key)) return prev
  return { ...prev, selectedKey: key, following: false }
}

/**
 * The host pulled the room onto something.
 *
 * Holds exactly as `choose` does, and for the same reason, but leaves the
 * viewer marked as following.
 */
export function follow(prev: StageSelection, key: string): StageSelection {
  if (!prev.arrivals.has(key)) return prev
  return { ...prev, selectedKey: key, following: true }
}

/** Drop the follow badge without changing what is on screen. */
export function stopFollowing(prev: StageSelection): StageSelection {
  return prev.following ? { ...prev, following: false } : prev
}
